import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Divider, Spin, Tag, Typography, message } from 'antd';
import { SaveOutlined, ReloadOutlined, CalendarOutlined, ThunderboltOutlined, SlackOutlined, CheckOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { appendSuffixIfMissing } from '../../Utils/usernameSuffix';
import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { fetchSlackNotifySettings, updateSlackNotifySettings } from '../../Modules/Account/Presentation/actions';
import {
  selectSlackNotifyError,
  selectSlackNotifyLoading,
  selectSlackNotifySaving,
  selectSlackNotifySettings,
} from '../../Modules/Account/Presentation/selectors';
import { SlackNotifyAction } from '../../Modules/Account/Model/SlackNotifySettings';

const { Title, Paragraph, Text } = Typography;

const dayOptions = [
  { label: '월', value: 0 },
  { label: '화', value: 1 },
  { label: '수', value: 2 },
  { label: '목', value: 3 },
  { label: '금', value: 4 },
  { label: '토', value: 5 },
  { label: '일', value: 6 },
] as const;

const actionOptions: Array<{ label: string; value: SlackNotifyAction; color: string; bg: string }> = [
  { label: '생성', value: 'create_slot', color: '#1677ff', bg: '#e6f7ff' },
  { label: '삭제', value: 'delete_slot', color: '#ff4d4f', bg: '#fff1f0' },
  { label: '수정', value: 'update_slot', color: '#fa8c16', bg: '#fff7e6' },
  { label: '연장', value: 'extend_slot', color: '#52c41a', bg: '#f6ffed' },
  { label: '환불', value: 'refund_slot', color: '#722ed1', bg: '#f9f0ff' },
];

const ALL_DAYS = dayOptions.map(o => o.value);
const ALL_ACTIONS = actionOptions.map(o => o.value);

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 48px auto;
  padding: 0 32px;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  .ant-card-body {
    padding: 40px 48px;
  }
`;

const OptionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  margin-left: 36px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 48px;
`;

const DayButton = styled.div<{ active: boolean }>`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  border: 1px solid #f0f0f0;
  color: #8c8c8c;
  background: #fafafa;

  ${({ active }) => active && css`
    background: #611f69;
    color: #fff;
    border-color: #611f69;
    box-shadow: 0 4px 12px rgba(97, 31, 105, 0.25);
    transform: translateY(-2px);
  `}

  &:hover {
    ${({ active }) => !active && css`
      border-color: #d9d9d9;
      color: #611f69;
      background: #fff;
    `}
  }
`;

const ActionButton = styled.div<{ active: boolean; $color: string; $bg: string }>`
  padding: 12px 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 15px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  border: 1px solid #f0f0f0;
  color: #595959;
  background: #fafafa;

  ${({ active, $color, $bg }) => active && css`
    background: ${$bg};
    color: ${$color};
    border-color: ${$color};
    box-shadow: 0 2px 8px ${$color}25;
    font-weight: 600;
    transform: translateY(-1px);
  `}

  &:hover {
    ${({ active, $color }) => !active && css`
      border-color: ${$color}40;
      color: ${$color};
      background: #fff;
    `}
  }
`;

const normalizeDays = (days: number[]) => {
  return Array.from(new Set(days))
    .filter(v => Number.isFinite(v) && v >= 0 && v <= 6)
    .sort((a, b) => a - b);
};

const normalizeActions = (actions: SlackNotifyAction[]) => {
  const set = new Set(actions);
  return actionOptions.map(o => o.value).filter(v => set.has(v));
};

const isSameArray = (a: Array<string | number>, b: Array<string | number>) => {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

const SlackNotifySettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectSlackNotifySettings);
  const loading = useAppSelector(selectSlackNotifyLoading);
  const saving = useAppSelector(selectSlackNotifySaving);
  const error = useAppSelector(selectSlackNotifyError);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [selectedActions, setSelectedActions] = useState<SlackNotifyAction[]>([]);

  const loadSettings = useCallback(() => {
    dispatch(fetchSlackNotifySettings());
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!settings) return;
    const normalizedDays = normalizeDays(
      Array.isArray(settings.slack_notify_days) ? settings.slack_notify_days : ALL_DAYS,
    );
    const normalizedActions = normalizeActions(
      Array.isArray(settings.slack_notify_actions) ? settings.slack_notify_actions : ALL_ACTIONS,
    );
    setSelectedDays(normalizedDays);
    setSelectedActions(normalizedActions);
  }, [settings]);

  const isDirty = useMemo(() => {
    if (!settings) return false;
    const baseDays = normalizeDays(settings.slack_notify_days);
    const baseActions = normalizeActions(settings.slack_notify_actions);
    const nextDays = normalizeDays(selectedDays);
    const nextActions = normalizeActions(selectedActions);
    return !isSameArray(baseDays, nextDays) || !isSameArray(baseActions, nextActions);
  }, [settings, selectedDays, selectedActions]);

  const handleSave = useCallback(async () => {
    try {
      const payload = {
        slack_notify_days: normalizeDays(selectedDays),
        slack_notify_actions: normalizeActions(selectedActions),
      };
      await dispatch(updateSlackNotifySettings(payload)).unwrap();
      message.success('설정이 저장되었습니다.');
    } catch (err) {
      const status = (err as any)?.status;
      if (status === 403) {
        message.error('대상 계정만 변경할 수 있습니다.');
      } else if (status === 400) {
        message.error('요일/유형 값이 올바르지 않습니다.');
      } else {
        message.error('설정 저장에 실패했습니다.');
      }
    }
  }, [dispatch, selectedDays, selectedActions]);

  const toggleDay = useCallback((day: number) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) return prev.filter(d => d !== day);
      return [...prev, day].sort((a, b) => a - b);
    });
  }, []);

  const toggleAction = useCallback((action: SlackNotifyAction) => {
    setSelectedActions(prev => {
      if (prev.includes(action)) return prev.filter(a => a !== action);
      return [...prev, action];
    });
  }, []);

  const daysAllChecked = selectedDays.length === ALL_DAYS.length;
  const daysIndeterminate = selectedDays.length > 0 && !daysAllChecked;
  const actionsAllChecked = selectedActions.length === ALL_ACTIONS.length;
  const actionsIndeterminate = selectedActions.length > 0 && !actionsAllChecked;

  const targetLabel = settings?.username
    ? appendSuffixIfMissing(settings.username)
    : 'adpa99@lift';

  if (loading && !settings) {
    return <Spin style={{ display: 'block', margin: '120px auto' }} />;
  }

  return (
    <Wrapper>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <SlackOutlined style={{ fontSize: 32, color: '#4A154B' }} />
          <Title level={2} style={{ margin: 0 }}>Slack 로그 알림 설정</Title>
        </div>
        <Paragraph type="secondary" style={{ fontSize: 16 }}>
          대상 계정: <Text strong>{targetLabel}</Text> · 알림 전송은 서버 백그라운드 작업으로 처리됩니다.
        </Paragraph>
      </Header>

      {error && (
        <Alert
          type="error"
          message="오류 발생"
          description={error}
          showIcon
          style={{ marginBottom: 24, borderRadius: 8 }}
        />
      )}

      <Spin spinning={loading}>
        <StyledCard bordered={false}>
          {/* 요일 설정 섹션 */}
          <div style={{ marginBottom: 40 }}>
            <SectionTitle>
              <CalendarOutlined style={{ color: '#1890ff', fontSize: 20 }} />
              <Text style={{ fontSize: 18 }}>알림 수신 요일</Text>
              <div style={{ flex: 1 }} />
              <Checkbox
                indeterminate={daysIndeterminate}
                checked={daysAllChecked}
                onChange={e => setSelectedDays(e.target.checked ? ALL_DAYS : [])}
              >
                전체 선택
              </Checkbox>
            </SectionTitle>
            
            <OptionGroup>
              {dayOptions.map(day => (
                <DayButton
                  key={day.value}
                  active={selectedDays.includes(day.value)}
                  onClick={() => toggleDay(day.value)}
                >
                  {day.label}
                </DayButton>
              ))}
            </OptionGroup>
            
            {selectedDays.length === 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 12, marginLeft: 28, fontSize: 14 }}>
                * 선택된 요일이 없으면 알림이 발송되지 않습니다.
              </Text>
            )}
          </div>

          <Divider style={{ margin: '32px 0' }} />

          {/* 알림 유형 설정 섹션 */}
          <div>
            <SectionTitle>
              <ThunderboltOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <Text style={{ fontSize: 18 }}>알림 수신 유형</Text>
              <div style={{ flex: 1 }} />
              <Checkbox
                indeterminate={actionsIndeterminate}
                checked={actionsAllChecked}
                onChange={e => setSelectedActions(e.target.checked ? ALL_ACTIONS : [])}
              >
                전체 선택
              </Checkbox>
            </SectionTitle>
            
            <OptionGroup>
              {actionOptions.map(action => (
                <ActionButton
                  key={action.value}
                  active={selectedActions.includes(action.value)}
                  $color={action.color}
                  $bg={action.bg}
                  onClick={() => toggleAction(action.value)}
                >
                  {selectedActions.includes(action.value) && <CheckOutlined />}
                  {action.label}
                </ActionButton>
              ))}
            </OptionGroup>

            {selectedActions.length === 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 12, marginLeft: 28, fontSize: 14 }}>
                * 선택된 유형이 없으면 알림이 발송되지 않습니다.
              </Text>
            )}
          </div>

          <ButtonGroup>
            {isDirty && <Tag color="warning" style={{ display: 'flex', alignItems: 'center', fontSize: 14, padding: '6px 12px' }}>저장되지 않은 변경사항이 있습니다</Tag>}
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadSettings} 
              disabled={loading || saving}
              size="large"
              style={{ height: 44 }}
            >
              초기화
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!settings || !isDirty}
              size="large"
              style={{ paddingLeft: 32, paddingRight: 32, height: 44, fontSize: 16 }}
            >
              설정 저장
            </Button>
          </ButtonGroup>
        </StyledCard>
      </Spin>
    </Wrapper>
  );
};

export default SlackNotifySettingsPage;
